import {
    interpolateInvoiceEmailVariables,
    invoiceEmailVariableToken,
    migrateLegacyInvoiceEmailTemplate,
} from "./invoice-email-variables";

// Shared builders for the native-invoice compose-step defaults. The drawer
// pre-fills from these; templates later regenerate from them when the stored
// subject/body are still automatic rather than user-authored.

export interface BuildDefaultEmailSubjectParams {
    businessName?: string | null;
    invoiceNumber?: string | null;
}

export interface BuildDefaultEmailBodyParams {
    invoiceNumber?: string | null;
    senderName?: string | null;
}

export interface IsAutomaticEmailSubjectParams extends BuildDefaultEmailSubjectParams {
    subject: string | null | undefined;
}

export interface IsAutomaticEmailMessageParams extends BuildDefaultEmailBodyParams {
    message: string | null | undefined;
}

export function buildDefaultEmailSubject({
    businessName,
    invoiceNumber,
}: BuildDefaultEmailSubjectParams): string {
    const trimmedName = businessName?.trim() ?? "";
    const trimmedNumber = invoiceNumber?.trim() ?? "";
    const namePart = trimmedName ? `${trimmedName} Invoice` : "Invoice";
    return trimmedNumber ? `${namePart} ${trimmedNumber}` : namePart;
}

export function buildDefaultEmailBody({
    invoiceNumber,
    senderName,
}: BuildDefaultEmailBodyParams): string {
    const trimmedNumber = invoiceNumber?.trim() ?? "";
    const invoiceLabel = trimmedNumber ? `Invoice ${trimmedNumber}` : "Invoice";
    const trimmedSender = senderName?.trim() ?? "";

    return [
        "Hi there,",
        "",
        `Please find attached ${invoiceLabel}.`,
        "",
        "Download the invoice to find due date and additional payment details.",
        "",
        "Best,",
        trimmedSender ? trimmedSender : null,
    ]
        .filter((line): line is string => line != null)
        .join("\n");
}

// The template-mode automatic defaults: the same builders fed with
// `{{entityName}}` / `{{invoiceNumber}}` tokens instead of concrete values.
export function buildTemplateEmailSubject(): string {
    return buildDefaultEmailSubject({
        businessName: invoiceEmailVariableToken("entityName"),
        invoiceNumber: invoiceEmailVariableToken("invoiceNumber"),
    });
}

export function buildTemplateEmailBody({
    senderName,
}: Pick<BuildDefaultEmailBodyParams, "senderName">): string {
    return buildDefaultEmailBody({
        invoiceNumber: invoiceEmailVariableToken("invoiceNumber"),
        senderName,
    });
}

export interface ResolveInvoiceEmailPlaceholdersParams {
    text: string;
    businessName?: string | null;
    invoiceNumber?: string | null;
    invoiceDate?: string | null;
    dueDate?: string | null;
}

// Replaces `{{entityName}}` / `{{invoiceNumber}}` / `{{invoiceDate}}` /
// `{{dueDate}}` (and the legacy `[entity]` / `[invoice-number]` / `[#]`
// spellings) with concrete values. A missing value keeps its token so a later
// pass can still resolve it. Dates render as `MMM d, yyyy` (e.g. Sep 17, 2026).
export function resolveInvoiceEmailPlaceholders({
    text,
    businessName,
    invoiceNumber,
    invoiceDate,
    dueDate,
}: ResolveInvoiceEmailPlaceholdersParams): string {
    return interpolateInvoiceEmailVariables(text, {
        entityName: businessName ?? null,
        invoiceNumber: invoiceNumber ?? null,
        invoiceDate: invoiceDate ?? null,
        dueDate: dueDate ?? null,
    });
}

export function isAutomaticEmailSubject({
    subject,
    businessName,
    invoiceNumber,
}: IsAutomaticEmailSubjectParams): boolean {
    if (subject == null || subject.trim() === "") {
        return true;
    }
    const candidates = [
        buildDefaultEmailSubject({ businessName, invoiceNumber }),
        buildTemplateEmailSubject(),
    ];
    return (
        candidates.includes(subject) ||
        candidates.includes(migrateLegacyInvoiceEmailTemplate(subject))
    );
}

export function isAutomaticEmailMessage({
    message,
    invoiceNumber,
    senderName,
}: IsAutomaticEmailMessageParams): boolean {
    if (message == null || message.trim() === "") {
        return true;
    }
    const candidates = [
        buildDefaultEmailBody({ invoiceNumber, senderName }),
        buildDefaultEmailBody({ invoiceNumber, senderName: null }),
        buildTemplateEmailBody({ senderName }),
        buildTemplateEmailBody({ senderName: null }),
    ];
    return (
        candidates.includes(message) ||
        candidates.includes(migrateLegacyInvoiceEmailTemplate(message))
    );
}
